import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Input, Modal, Notice, Panel, Select } from '../../design/index.js'
import { useApi } from '../../app/api.js'
import { ErrorBlock, Loading, messageFor } from '../../framework/feedback.js'
import { formatDate, formatDateTime } from '../../framework/format.js'
import './admin.css'

interface OAuthView { clientId: string | null; hasClientSecret: boolean; hasRefreshToken: boolean; tokenExpiresAt: string | null; available: boolean }
interface MappingRule { kind: 'ITEM_CATEGORY' | 'TAX_PCT' | 'PARTY' | 'DOC_TYPE'; from: string; to: string; note?: string }
interface CompanyIntegration {
  companyId: string; companyName: string; isTraining: boolean; connector: string; active: boolean
  authorisedByName: string | null; authorisedAt: string | null; transactionsFrom: string | null
}

const KIND_WORDS: Readonly<Record<MappingRule['kind'], { label: string; from: string; to: string }>> = {
  ITEM_CATEGORY: { label: 'Item category → account', from: 'Item category id', to: 'Account in the books' },
  TAX_PCT: { label: 'Tax rate → tax code', from: 'Rate, e.g. 18', to: 'Tax code in the books' },
  PARTY: { label: 'Party → contact', from: 'Party id', to: 'Contact id in the books' },
  DOC_TYPE: { label: 'Document type → voucher type', from: 'Document type, e.g. purchase_invoice', to: 'Voucher type' },
}

/**
 * The parts of the reference's ERP/Accounting Integration screen beyond the
 * connector itself (INT-007, INT-008, INT-009): the Zoho OAuth grant, Remove
 * the integration, the transformation rules, and Start/Pause per company.
 */
export const IntegrationExtras = ({ connector }: { connector: string }) => {
  const api = useApi()
  const client = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [secret, setSecret] = useState('')
  const [showSecret, setShowSecret] = useState(false)
  const [clientId, setClientId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [removing, setRemoving] = useState(false)
  const [rules, setRules] = useState<MappingRule[] | null>(null)

  const oauth = useQuery({ queryKey: ['integrationOAuth', api.companyId], queryFn: () => api.send<OAuthView>('/integration/oauth') })
  const companies = useQuery({ queryKey: ['integrationCompanies', api.companyId], queryFn: () => api.send<CompanyIntegration[]>('/integration/companies') })
  const mappings = useQuery({ queryKey: ['integrationMappings', api.companyId], queryFn: () => api.send<MappingRule[]>('/integration/mappings') })

  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      for (const k of ['integrationOAuth', 'integrationCompanies', 'integrationMappings', 'integrationConfig']) {
        void client.invalidateQueries({ queryKey: [k, api.companyId] })
      }
      setError(null); setSecret(''); setCode(''); setRemoving(false); setRules(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (oauth.isLoading || companies.isLoading) return <Loading />
  if (oauth.error) return <ErrorBlock error={oauth.error} />
  const o = oauth.data!
  const id = clientId ?? o.clientId ?? ''

  return (
    <>
      {error ? <div style={{ margin: '12px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      {connector === 'ZOHO_BOOKS' ? (
        <Panel title="Zoho Books authorisation" aside={o.hasRefreshToken ? 'Connected' : 'Not authorised yet'}>
          {!o.available ? (
            <Notice kind="info">Authorising with Zoho needs this server registered with Zoho first. Until then, paste an access token above.</Notice>
          ) : null}
          <div className="form-grid">
            <Field label="Client ID" required><Input value={id} onChange={setClientId} /></Field>
            <Field label="Client Secret" required hint={o.hasClientSecret ? 'Stored. Type a new one to replace it.' : undefined}>
              <div className="row">
                <Input type={showSecret ? 'text' : 'password'} value={secret} onChange={setSecret} aria-label="Client Secret" />
                <Button kind="ghost" aria-label={showSecret ? 'Hide the secret' : 'Show the secret'} onClick={() => setShowSecret(!showSecret)}>
                  {showSecret ? 'Hide' : 'Show'}
                </Button>
              </div>
            </Field>
          </div>
          <div className="row" style={{ marginBottom: 12 }}>
            <Button kind="ghost" disabled={run.isPending || id.trim() === ''}
              onClick={() => run.mutate(() => api.send('/integration/oauth/client', { clientId: id, ...(secret ? { clientSecret: secret } : {}) }))}>
              Save client
            </Button>
            <Button kind="ghost" disabled={run.isPending || !o.available}
              onClick={() => run.mutate(async () => {
                const { url } = await api.send<{ url: string }>('/integration/oauth/authorize')
                window.open(url, '_blank', 'noopener')
              })}>
              Generate Code
            </Button>
          </div>
          <Field label="Authorization Code" hint="After you allow access, Zoho opens a page. Paste that page's whole address: it carries the code and the check that it came from this Generate Code.">
            <div className="row">
              <Input value={code} onChange={setCode} />
              <Button kind="primary" disabled={run.isPending || code.trim().length < 4 || !o.available}
                onClick={() => run.mutate(() => api.send('/integration/oauth/code', { code }))}>Connect</Button>
            </div>
          </Field>
          {o.tokenExpiresAt ? <p className="helper">Access renews itself; the current token runs to {formatDateTime(o.tokenExpiresAt)}.</p> : null}
        </Panel>
      ) : null}

      <Panel title="Transformation" aside={<Button kind="ghost" onClick={() => setRules([...(mappings.data ?? [])])}>View/Edit transformation rules</Button>}>
        <p className="helper">
          {(mappings.data ?? []).length === 0
            ? 'No rules yet: documents go across as they are.'
            : `${(mappings.data ?? []).length} rule(s) map item categories, tax rates, parties and document types to what the books call them.`}
        </p>
      </Panel>

      <Panel title="Start/Pause Integration for companies">
        <div className="card-list">
          {(companies.data ?? []).map((c) => (
            <div className="card-item" key={c.companyId}>
              <div>
                <div className="card-item__title">{c.companyName}{c.isTraining ? ' (training)' : ''}</div>
                <div className="cell-sub">
                  {c.active
                    ? `Authorized by: ${c.authorisedByName ?? '—'} | Authorized On: ${c.authorisedAt ? formatDateTime(c.authorisedAt) : '—'} | Transaction from: ${c.transactionsFrom ? formatDate(c.transactionsFrom) : '—'}`
                    : c.connector === 'NONE' ? 'Not connected' : 'Paused'}
                </div>
              </div>
              <span className="row">
                <Button kind="ghost" disabled={run.isPending || c.active || c.isTraining}
                  onClick={() => run.mutate(() => api.send(`/integration/companies/${c.companyId}/start`, {}))}>Start</Button>
                <Button kind="ghost" disabled={run.isPending || !c.active}
                  onClick={() => run.mutate(() => api.send(`/integration/companies/${c.companyId}/pause`, {}))}>Pause</Button>
              </span>
            </div>
          ))}
        </div>
      </Panel>

      {connector !== 'NONE' ? (
        <div className="row" style={{ margin: '0 0 20px' }}>
          <Button kind="ghost" onClick={() => setRemoving(true)}>Remove the integration</Button>
        </div>
      ) : null}

      {removing ? (
        <Modal title="Remove the integration?" tone="warn" onClose={() => setRemoving(false)}
          actions={<><Button kind="ghost" onClick={() => setRemoving(false)}>Keep it</Button>
            <Button kind="primary" disabled={run.isPending} onClick={() => run.mutate(() => api.send('/integration/remove', {}))}>Remove it</Button></>}>
          <p>Nothing more is sent, and every stored credential — token, client secret — is forgotten. What was already sent stays sent.</p>
        </Modal>
      ) : null}

      {rules ? (
        <Modal title="Transformation rules" wide onClose={() => setRules(null)}
          actions={<><Button kind="ghost" onClick={() => setRules(null)}>Cancel</Button>
            <Button kind="primary" disabled={run.isPending} onClick={() => run.mutate(() => api.send('/integration/mappings', { rules }))}>Save</Button></>}>
          <table className="scenario-table">
            <thead><tr><th>Maps</th><th>From</th><th>To</th><th /></tr></thead>
            <tbody>
              {rules.map((r, i) => (
                <tr key={i}>
                  <td><Select value={r.kind} aria-label={`Rule ${i + 1} kind`}
                    onChange={(kind) => setRules(rules.map((x, k) => (k === i ? { ...x, kind: kind as MappingRule['kind'] } : x)))}
                    options={Object.entries(KIND_WORDS).map(([value, w]) => ({ value, label: w.label }))} /></td>
                  <td><Input value={r.from} aria-label={`Rule ${i + 1} from`} placeholder={KIND_WORDS[r.kind].from}
                    onChange={(from) => setRules(rules.map((x, k) => (k === i ? { ...x, from } : x)))} /></td>
                  <td><Input value={r.to} aria-label={`Rule ${i + 1} to`} placeholder={KIND_WORDS[r.kind].to}
                    onChange={(to) => setRules(rules.map((x, k) => (k === i ? { ...x, to } : x)))} /></td>
                  <td><Button kind="ghost" onClick={() => setRules(rules.filter((_, k) => k !== i))}>Remove</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button kind="ghost" onClick={() => setRules([...rules, { kind: 'TAX_PCT', from: '', to: '' }])}>Add a rule</Button>
        </Modal>
      ) : null}
    </>
  )
}
